import { TranslocoService } from '@ngneat/transloco';

import { OverlayContainer } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatStepperModule } from '@angular/material/stepper';

import { SnFormModule } from '@lib/form';
import { SnFormRadioModule } from '@lib/form/radio';
import { SnOperationalViewsModule } from '@lib/operational-views';
import { TranslationModule, AVAILABLE_LANGUAGES } from '@lib/translation';
import { SnIconModule } from '@lib/ui/icon';
import { ExistingFullscreenOverlayContainer } from '@lib/ui/overlay';

import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule, RegistryTelemetryService } from './core';
import { SnRegistryGaurd } from './registry.gaurd';
import { SharedModule } from './shared/shared.module';

// Boot the Faro/PCG transport once at startup (FWX-5881). init() is
// fire-and-forget inside the service, so returning void keeps app bootstrap
// from waiting on the telemetry transport.
export function initTelemetryFactory(telemetry: RegistryTelemetryService): () => void {
  return () => telemetry.init();
}

function detectInitialLanguage(available: string[]): string {
  const urlLang = new URLSearchParams(window.location.search).get('lang');
  if (urlLang && available.includes(urlLang)) {
    return urlLang;
  }

  const stored = localStorage.getItem('fusus_registry_language');
  if (stored && available.includes(stored)) {
    return stored;
  }

  const browserLangs = navigator.languages ?? [navigator.language];
  for (const browserLang of browserLangs) {
    if (available.includes(browserLang)) {
      return browserLang;
    }
    const prefix = browserLang.split('-')[0];
    const match = available.find((a) => a.startsWith(prefix + '-') && a !== 'en-XA');
    if (match) {
      return match;
    }
  }

  return 'en-US';
}

const INITIAL_LANG = detectInitialLanguage(AVAILABLE_LANGUAGES);
localStorage.setItem('fusus_registry_language', INITIAL_LANG);

@NgModule({
  imports: [
    CoreModule,
    TranslationModule.forRoot({
      availableLangs: AVAILABLE_LANGUAGES,
      defaultLang: INITIAL_LANG,
      fallbackLang: 'en-US',
      reRenderOnLangChange: true,
      prodMode: environment.production,
    }),
    AppRoutingModule,
    SnFormModule,
    SnFormRadioModule,
    CommonModule,
    FormsModule,
    MatStepperModule,
    MatProgressBarModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    SnIconModule,
    SnOperationalViewsModule,
    SharedModule,
  ],
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  providers: [
    SnRegistryGaurd,
    { provide: OverlayContainer, useClass: ExistingFullscreenOverlayContainer },
    {
      provide: APP_INITIALIZER,
      useFactory: initTelemetryFactory,
      deps: [RegistryTelemetryService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (transloco: TranslocoService) => {
        return () => {
          transloco.setActiveLang(INITIAL_LANG);
          return transloco.load(INITIAL_LANG).toPromise();
        };
      },
      deps: [TranslocoService],
      multi: true,
    },
  ],
})
export class AppModule {}
